/**
 * Pool of arithmetic challenges for the parent gate.
 *
 * Answers are between 5 and 20 — high enough that a toddler bashing the
 * screen has only a ~1/4 random-tap success rate (and ~1/16 across two
 * gate-protected actions). Low enough that an adult sees the answer
 * immediately and recognizes the affordance.
 */
export interface Equation {
  a: number;
  b: number;
  answer: number;
}

export const PARENT_GATE_EQUATIONS: readonly Equation[] = [
  { a: 2, b: 3, answer: 5 },
  { a: 3, b: 4, answer: 7 },
  { a: 4, b: 5, answer: 9 },
  { a: 5, b: 6, answer: 11 },
  { a: 6, b: 7, answer: 13 },
  { a: 4, b: 8, answer: 12 },
  { a: 7, b: 8, answer: 15 },
  { a: 9, b: 5, answer: 14 },
  { a: 6, b: 8, answer: 14 },
  { a: 7, b: 9, answer: 16 },
];

export function pickEquation(rng: () => number = Math.random): Equation {
  return PARENT_GATE_EQUATIONS[Math.floor(rng() * PARENT_GATE_EQUATIONS.length)]!;
}

/**
 * Build a 4-tile choice set: the correct answer + 3 plausible distractors.
 * Distractors are within ±5 of the answer, never negative, never the answer.
 */
export function pickGateChoices(answer: number, rng: () => number = Math.random): number[] {
  const candidates = new Set<number>();
  // Off-by-one and off-by-two are the most plausible toddler taps.
  for (const delta of [1, -1, 2, -2, 3, -3, 4, -4]) {
    const v = answer + delta;
    if (v >= 1 && v !== answer) candidates.add(v);
  }
  const pool = Array.from(candidates);
  const distractors: number[] = [];
  while (distractors.length < 3 && pool.length > 0) {
    const i = Math.floor(rng() * pool.length);
    const [picked] = pool.splice(i, 1);
    distractors.push(picked!);
  }
  const tiles = [answer, ...distractors];
  // Shuffle.
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = tiles[i]!;
    const b = tiles[j]!;
    tiles[i] = b;
    tiles[j] = a;
  }
  return tiles;
}
